// src/features/execucao/popups/FichaExecucaoModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FichaExecucaoModal.css";

import { salvarExecucao } from "../api";
import EditarModal from "./EditarModal";
import ReprogramarModal from "./ReprogramarModal";
import FechamentoModal from "./FechamentoModal";

/* ===== Helpers tempo ===== */
const canonTimeHHMMSS = (raw) => {
  if (!raw && raw !== 0) return "";
  const m = String(raw).trim().match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!m) return "";
  const hh = String(Number(m[1])).padStart(2, "0");
  const mm = m[2];
  const ss = m[3] || "00";
  return `${hh}:${mm}:${ss}`;
};
const toHHMMInput = (raw) => canonTimeHHMMSS(raw).slice(0, 5) || "";
const toMinutes = (any) => {
  const m = String(any || "").trim().match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};
const sumIntervalsMinutes = (obj) => {
  const P = [
    ["primeiro_inicio", "primeiro_fim"],
    ["segundo_inicio", "segundo_fim"],
    ["terceiro_inicio", "terceiro_fim"],
  ];
  let t = 0;
  for (const [a, b] of P) {
    const A = toMinutes(obj?.[a]);
    const B = toMinutes(obj?.[b]);
    if (A != null && B != null && B > A) t += B - A;
  }
  return t;
};
const toHHMM = (mins) => {
  if (!mins || mins <= 0) return "00:00";
  const h = String(Math.floor(mins / 60)).padStart(2, "0");
  const m = String(mins % 60).padStart(2, "0");
  return `${h}:${m}`;
};
const toHHMMSSFromMinutes = (mins) => `${toHHMM(mins)}:00`;
const toHHMMShow = (raw) => toHHMMInput(raw) || "";

/* ===== Helpers numéricos pt-BR ===== */
const parsePtBRNumber = (text) => {
  if (text == null || text === "") return null;
  const normalized = String(text).replace(/\s+/g, "").replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};
const formatPtBRNumber = (val) => {
  if (val == null || val === "") return "";
  const num = typeof val === "number" ? val : parsePtBRNumber(val);
  if (num == null) return "";
  return new Intl.NumberFormat("pt-BR").format(num);
};
const clamp0 = (n) => Math.max(0, Number(n || 0));

/* ===== "Corretor ortográfico" leve ===== */
const CORRECOES = [
  [/\brpévia\b/gi, "prévia"],
  [/\bprevia\b/gi, "prévia"],
  [/\bprim[ée]ria\b/gi, "primeira"],
  [/\bsegubda\b/gi, "segunda"],
  [/\binicio\b/gi, "início"],
  [/\btermino\b/gi, "término"],
  [/\bobs\b/gi, "observação"],
];
const corrigePT = (txt = "") => {
  let out = String(txt || "");
  for (const [re, rep] of CORRECOES) out = out.replace(re, rep);
  return out.replace(/\s{2,}/g, " ").trim();
};

export default function FichaExecucaoModal({
  open,
  programacao = {},
  onClose = () => {},
  onSaved = () => {},
}) {
  if (open === false) return null;

  const tempoDoBanco = canonTimeHHMMSS(programacao?.tempo_total || programacao?.total_tempo || "");
  const initialQtdNum = parsePtBRNumber(programacao.quantidade_real ?? programacao.qtd ?? "");

  const [dados, setDados] = useState({
    ...programacao,
    primeiro_inicio: toHHMMInput(programacao.primeiro_inicio),
    primeiro_fim: toHHMMInput(programacao.primeiro_fim),
    segundo_inicio: toHHMMInput(programacao.segundo_inicio),
    segundo_fim: toHHMMInput(programacao.segundo_fim),
    terceiro_inicio: toHHMMInput(programacao.terceiro_inicio),
    terceiro_fim: toHHMMInput(programacao.terceiro_fim),
    tempo_total: tempoDoBanco || "",
    quantidade_real: initialQtdNum ?? 0,
    status_finalizacao: programacao.status_finalizacao || "em_andamento",
    observacao: programacao.observacao || "",
    responsavel: programacao.responsavel || "",
    impacto_fechamento: programacao.impacto_fechamento || "",
    causa_fechamento: programacao.causa_fechamento || "",
    justificativa_fechamento: programacao.justificativa_fechamento || "",
    unidade_medida: programacao.unidade_medida || programacao.medida || "",
    sub: programacao.sub || programacao.Sub || programacao.SUB || "",
  });

  const [qtdText, setQtdText] = useState(
    initialQtdNum == null ? "0" : formatPtBRNumber(clamp0(initialQtdNum))
  );

  const [salvando, setSalvando] = useState(false);
  const [editarPopupAberto, setEditarPopupAberto] = useState(false);
  const [reprogramarPopupAberto, setReprogramarPopupAberto] = useState(false);
  const [fechamentoPopupAberto, setFechamentoPopupAberto] = useState(false);
  const firstLoad = useRef(true);

  /* ===== NÃO fecha por ESC ===== */
  // (intencionalmente sem listener de Escape)

  /* ===== Cálculo e sincronização do tempo total ===== */
  const tempoTotalMinutos = useMemo(
    () =>
      sumIntervalsMinutes({
        primeiro_inicio: dados.primeiro_inicio,
        primeiro_fim: dados.primeiro_fim,
        segundo_inicio: dados.segundo_inicio,
        segundo_fim: dados.segundo_fim,
        terceiro_inicio: dados.terceiro_inicio,
        terceiro_fim: dados.terceiro_fim,
      }),
    [
      dados.primeiro_inicio,
      dados.primeiro_fim,
      dados.segundo_inicio,
      dados.segundo_fim,
      dados.terceiro_inicio,
      dados.terceiro_fim,
    ]
  );

  useEffect(() => {
    const algum = [
      dados.primeiro_inicio,
      dados.primeiro_fim,
      dados.segundo_inicio,
      dados.segundo_fim,
      dados.terceiro_inicio,
      dados.terceiro_fim,
    ].some((v) => (v || "").length >= 4);

    if (algum) {
      setDados((prev) => ({
        ...prev,
        tempo_total: toHHMMSSFromMinutes(tempoTotalMinutos),
      }));
    } else if (firstLoad.current) {
      firstLoad.current = false;
      setDados((prev) => ({
        ...prev,
        tempo_total: tempoDoBanco || prev.tempo_total || "",
      }));
    }
  }, [
    tempoTotalMinutos,
    dados.primeiro_inicio,
    dados.primeiro_fim,
    dados.segundo_inicio,
    dados.segundo_fim,
    dados.terceiro_inicio,
    dados.terceiro_fim,
    tempoDoBanco,
  ]);

  const tempoTotalParaMostrar = useMemo(() => {
    const algum = [
      dados.primeiro_inicio,
      dados.primeiro_fim,
      dados.segundo_inicio,
      dados.segundo_fim,
      dados.terceiro_inicio,
      dados.terceiro_fim,
    ].some((v) => (v || "").length >= 4);
    if (algum && tempoTotalMinutos > 0) return toHHMM(tempoTotalMinutos);
    return (
      (canonTimeHHMMSS(dados.tempo_total) ||
        canonTimeHHMMSS(tempoDoBanco) ||
        "00:00").slice(0, 5)
    );
  }, [tempoTotalMinutos, dados, tempoDoBanco]);

  const estaFinalizado =
    (dados.status_finalizacao || "").toLowerCase() === "finalizado";

  const definirStatusFinalizacao = () => {
    if ((dados.status_finalizacao || "").toLowerCase() === "finalizado")
      return "finalizado";
    if ((dados.segunda_previa || "").trim()) return "segunda_previa";
    if ((dados.primeira_previa || "").trim()) return "primeira_previa";
    return dados.status_finalizacao || "em_andamento";
  };

  /* ===== Validação (silenciosa) ===== */
  const validaAntesDeFinalizar = (val, fechamentoForm) => {
    const missing = [];
    const errors = [];

    const qtd = clamp0(parsePtBRNumber(val.quantidade_real));
    if (!(qtd > 0)) missing.push("Quantidade Realizada (> 0)");

    const i1 = toMinutes(val.primeiro_inicio);
    const f1 = toMinutes(val.primeiro_fim);
    if (i1 == null) missing.push("Início 1");
    if (f1 == null) missing.push("Término 1");
    if (i1 != null && f1 != null && f1 <= i1) {
      errors.push("Início 1 deve ser antes de Término 1");
    }

    if (!String(val.primeira_previa || "").trim()) missing.push("Primeira Prévia");
    if (!String(val.segunda_previa || "").trim())  missing.push("Segunda Prévia");

    const resp = (fechamentoForm?.responsavel || val.responsavel || "").trim();
    const imp  = (fechamentoForm?.impacto_fechamento || val.impacto_fechamento || "").trim();
    const cau  = (fechamentoForm?.causa_fechamento || val.causa_fechamento || "").trim();
    const jus  = (fechamentoForm?.justificativa_fechamento || val.justificativa_fechamento || "").trim();
    if (!resp) missing.push("Responsabilidade (Fechamento)");
    if (!imp)  missing.push("Impacto (Fechamento)");
    if (!cau)  missing.push("Causa (Fechamento)");
    if (!jus)  missing.push("Justificativa (Fechamento)");

    return { ok: missing.length === 0 && errors.length === 0, missing, errors };
  };

  /* ===== Handlers ===== */
  const handleChange = (e) => {
    if (estaFinalizado) return;
    const { name, value } = e.target;

    if (name === "quantidade_real") {
      let text = value.replace(/[^\d.,]/g, "");
      if (text.trim() === "") text = "0";
      const num = clamp0(parsePtBRNumber(text));
      setQtdText(text);
      setDados((p) => ({ ...p, quantidade_real: num }));
      return;
    }

    setDados((p) => ({ ...p, [name]: value }));
  };

  const handleQtdBlur = () => {
    const num = clamp0(parsePtBRNumber(qtdText));
    setQtdText(formatPtBRNumber(num) || "0");
  };

  const handleTextBlurCorrigir = (e) => {
    const { name, value } = e.target;
    if (!["primeira_previa", "segunda_previa", "observacao"].includes(name)) return;
    const fix = corrigePT(value);
    if (fix !== value) {
      setDados((p) => ({ ...p, [name]: fix }));
    }
  };

  /* ===== Sanitização e persistência ===== */
  const sanitizePayload = (raw) => {
    const allowed = new Set([
      "codigo_unico",
      "primeira_previa",
      "segunda_previa",
      "observacao",
      "primeiro_inicio",
      "primeiro_fim",
      "segundo_inicio",
      "segundo_fim",
      "terceiro_inicio",
      "terceiro_fim",
      "tempo_total",
      "quantidade_real",
      "unidade_medida",
      "status_finalizacao",
      "status_programacaod1",
      "status_workflow",
      "data",
      "responsavel",
      "impacto_fechamento",
      "causa_fechamento",
      "justificativa_fechamento",
      "motivo_alteracao",
      "sub",
      "etapa_nova",
    ]);
    const clean = {};
    for (const [k, v] of Object.entries(raw)) {
      if (!allowed.has(k)) continue;
      if (v === undefined) continue;
      clean[k] = v;
    }
    if (!clean.unidade_medida) delete clean.unidade_medida;
    return clean;
  };

  const salvar = async (customStatus = null, fechamentoForm = null) => {
    if (salvando) return false;
    setSalvando(true);
    try {
      const dadosCorrigidos = {
        ...dados,
        primeira_previa: corrigePT(dados.primeira_previa || ""),
        segunda_previa:  corrigePT(dados.segunda_previa  || ""),
        observacao:      corrigePT(dados.observacao      || ""),
      };

      if (customStatus === "finalizado") {
        const { ok } = validaAntesDeFinalizar(dadosCorrigidos, fechamentoForm);
        if (!ok) return false; // FechamentoModal já mostra o aviso
      }

      let statusFinal = definirStatusFinalizacao();
      let motivoAlteracao = "Atualização da execução";
      let statusWorkflow;

      if (customStatus === "finalizado") {
        statusFinal = "finalizado";
        statusWorkflow = "finalizado";
        motivoAlteracao = "Finalizado na execução";
        if (fechamentoForm) {
          setDados((prev) => ({ ...prev, ...fechamentoForm }));
        }
      }

      const algum = [
        dadosCorrigidos.primeiro_inicio,
        dadosCorrigidos.primeiro_fim,
        dadosCorrigidos.segundo_inicio,
        dadosCorrigidos.segundo_fim,
        dadosCorrigidos.terceiro_inicio,
        dadosCorrigidos.terceiro_fim,
      ].some((v) => (v || "").length >= 4);

      const tempoTotalParaSalvar = algum
        ? toHHMMSSFromMinutes(tempoTotalMinutos)
        : canonTimeHHMMSS(dadosCorrigidos.tempo_total) ||
          canonTimeHHMMSS(tempoDoBanco) ||
          null;

      const payload = sanitizePayload({
        ...dadosCorrigidos,
        ...(fechamentoForm || {}),
        tempo_total: tempoTotalParaSalvar,
        status_finalizacao: statusFinal,
        status_workflow: statusWorkflow,
        codigo_unico: dados.codigo_unico,
        motivo_alteracao: motivoAlteracao,
      });

      await salvarExecucao(payload);
      onSaved();
      return true;
    } catch (e) {
      alert("Erro ao salvar: " + (e?.response?.data?.error || e.message || "Erro desconhecido"));
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const salvarReprogramacao = async (form) => {
    if (salvando) return;
    setSalvando(true);
    try {
      await salvarExecucao(
        sanitizePayload({
          codigo_unico: dados.codigo_unico,
          etapa_nova: "d1",
          status_programacaod1: "reprogramar",
          motivo_alteracao: form.justificativa || "Reprogramação solicitada",
          responsavel: form.responsabilidade,
          impacto_fechamento: form.impacto,
          causa_fechamento: form.causa,
          justificativa_fechamento: corrigePT(form.justificativa),
          status_finalizacao: null,
          // limpeza de campos de execução:
          primeira_previa: null,
          segunda_previa: null,
          primeiro_inicio: null,
          primeiro_fim: null,
          segundo_inicio: null,
          segundo_fim: null,
          terceiro_inicio: null,
          terceiro_fim: null,
          quantidade_real: null,
          tempo_total: null,
        })
      );
      setReprogramarPopupAberto(false);
      onSaved();
    } catch (e) {
      alert("Erro ao reprogramar: " + (e?.response?.data?.error || e.message || "Erro desconhecido"));
    } finally {
      setSalvando(false);
    }
  };

  const InfoField = ({ label, value }) => (
    <div className="exec-ficha__item">
      <div className="exec-ficha__lbl">{label}</div>
      <div className="exec-ficha__val">
        <span className="exec-ficha__readonly">{value || "—"}</span>
      </div>
    </div>
  );

  return (
    <div className="exec-ficha">
      {/* Backdrop sem onClick — NÃO fecha clicando fora */}
      <div className="exec-ficha__backdrop">
        <div
          className="exec-ficha__modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Ficha da Execução"
        >
          <div className="exec-ficha__header">
            <h3 className="exec-ficha__title">Ficha da Execução</h3>
            <button className="exec-ficha__close" onClick={onClose} aria-label="Fechar">×</button>
          </div>

          <div className="exec-ficha__scroller">
            {/* === Grid — ORDEM NOVA === */}
            <div className="exec-ficha__grid">
              <InfoField label="Atividade" value={dados.atividade} />
              <InfoField label="Ativo" value={dados.ativo} />
              <InfoField label="Unidade de Medida" value={dados.unidade_medida || dados.medida || ""} />

              <InfoField label="Gerência" value={dados.gerencia} />
              <InfoField label="Coordenador" value={dados.coordenador} />
              <InfoField label="Sub" value={dados.sub} />

              <InfoField label="KM de Início" value={dados.kmInicio} />
              <InfoField label="KM Final" value={dados.kmFinal} />
              <InfoField label="Quantidade" value={dados.qtd} />

              <InfoField label="Horário do Intervalo" value={toHHMMShow(dados.horario_do_intervalo)} />
              <InfoField label="Tempo do Intervalo"   value={toHHMMShow(dados.tempo_do_intervalo)} />
              <InfoField label="Prioridade" value={dados.prioridade} />

              <InfoField label="Pátio de Origem" value={dados.sbOrigem} />
              <InfoField label="SB de Trabalho" value={dados.sbDeTrabalho} />
              <InfoField label="Pátio de Encerramento" value={dados.sbEncerramento} />

              <InfoField label="Quantidade de Maquinista" value={dados.quantidade_maquinista} />
              <InfoField label="Maquinista" value={dados.maquinista} />
              <InfoField label="Tipo de Acionamento" value={dados.tipo_acionamento} />

              <InfoField label="Tipo de Programação" value={dados.tipo_programacao} />
              <InfoField label="Locomotiva 1" value={dados.locomotiva1} />
              <InfoField label="Locomotiva 2" value={dados.locomotiva2} />
            </div>

            {/* OBSERVAÇÃO — somente leitura, altura menor */}
            <div className="exec-ficha__item is-span-2 is-textarea is-observacao" style={{ marginTop: 15 }}>
              <div className="exec-ficha__lbl">Observação</div>
              <div className="exec-ficha__val">
                <textarea
                  name="observacao"
                  value={dados.observacao || ""}
                  readOnly
                  disabled
                  aria-readonly="true"
                  tabIndex={-1}
                  onChange={() => {}}
                  onBlur={() => {}}
                />
              </div>
            </div>

            {/* Seção Execução */}
            <div className="exec-ficha__subtitulo">EXECUÇÃO</div>

            <div className="exec-ficha__item is-span-2 is-textarea">
              <div className="exec-ficha__lbl">Primeira Prévia</div>
              <div className="exec-ficha__val">
                <textarea
                  name="primeira_previa"
                  value={dados.primeira_previa || ""}
                  onChange={handleChange}
                  onBlur={handleTextBlurCorrigir}
                />
              </div>
            </div>

            <div className="exec-ficha__grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 15 }}>
              {[
                ["primeiro_inicio", "Início 1"],
                ["primeiro_fim", "Término 1"],
                ["segundo_inicio", "Início 2"],
                ["segundo_fim", "Término 2"],
                ["terceiro_inicio", "Início 3"],
                ["terceiro_fim", "Término 3"],
              ].map(([name, label]) => (
                <div className="exec-ficha__item" key={name}>
                  <div className="exec-ficha__lbl">{label}</div>
                  <div className="exec-ficha__val">
                    <input type="time" name={name} value={dados[name] || ""} onChange={handleChange} />
                  </div>
                </div>
              ))}
            </div>

            <div className="exec-ficha__grid" style={{ marginTop: 15 }}>
              <div className="exec-ficha__item">
                <div className="exec-ficha__lbl">Total de Horas Realizadas</div>
                <div className="exec-ficha__val">
                  <span className="exec-ficha__readonly">{tempoTotalParaMostrar}</span>
                </div>
              </div>

              <div className="exec-ficha__item">
                <div className="exec-ficha__lbl">Quantidade Realizada</div>
                <div className="exec-ficha__val">
                  <input
                    type="text"
                    name="quantidade_real"
                    inputMode="numeric"
                    pattern="[0-9\.,]*"
                    value={qtdText}
                    onChange={handleChange}
                    onBlur={handleQtdBlur}
                  />
                </div>
              </div>

              <div className="exec-ficha__item">
                <div className="exec-ficha__lbl">Unidade de Medida</div>
                <div className="exec-ficha__val">
                  <span className="exec-ficha__readonly">
                    {dados.unidade_medida || dados.medida || ""}
                  </span>
                </div>
              </div>
            </div>

            <div className="exec-ficha__item is-span-2 is-textarea" style={{ marginTop: 15 }}>
              <div className="exec-ficha__lbl">Segunda Prévia</div>
              <div className="exec-ficha__val">
                <textarea
                  name="segunda_previa"
                  value={dados.segunda_previa || ""}
                  onChange={handleChange}
                  onBlur={handleTextBlurCorrigir}
                />
              </div>
            </div>
          </div>

          {!estaFinalizado && (
            <div className="exec-ficha__footer">
              <button className="exec-ficha__btn" onClick={() => setEditarPopupAberto(true)} disabled={salvando}>
                Editar
              </button>
              <button className="exec-ficha__btn" onClick={() => setReprogramarPopupAberto(true)} disabled={salvando}>
                Reprogramar
              </button>
              <button className="exec-ficha__btn" onClick={() => salvar()} disabled={salvando}>
                {salvando ? "Salvando..." : "Salvar"}
              </button>
              <button className="exec-ficha__btn" onClick={() => setFechamentoPopupAberto(true)} disabled={salvando}>
                Finalizar
              </button>
            </div>
          )}
        </div>
      </div>

      {editarPopupAberto && (
        <EditarModal
          programacao={dados}
          onClose={() => setEditarPopupAberto(false)}
          onSaved={(atualizado) => {
            setDados((prev) => ({
              ...prev,
              ...atualizado,
              unidade_medida:
                atualizado.unidade_medida ||
                atualizado.medida ||
                prev.unidade_medida ||
                prev.medida ||
                "",
            }));
            setEditarPopupAberto(false);
            onSaved();
          }}
        />
      )}

      {reprogramarPopupAberto && (
        <ReprogramarModal
          onClose={() => setReprogramarPopupAberto(false)}
          onConfirm={(form) => salvarReprogramacao(form)}
        />
      )}

      {fechamentoPopupAberto && (
        <FechamentoModal
          initial={dados}
          onClose={() => setFechamentoPopupAberto(false)}
          onConfirm={async (form) => {
            const ok = await salvar("finalizado", form);
            if (ok) setFechamentoPopupAberto(false);
          }}
        />
      )}
    </div>
  );
}
